import React from 'react';
import { Typography, Grid, Button, TextField } from '@mui/material';
import validate from 'validate.js';
import axios from "axios";
import { useSnackbar } from 'notistack';
import { styled } from '@mui/system';

const Root = styled('div')({
  width: '100%',
});

const schema = {
  fullname: {
    presence: { allowEmpty: false, message: 'is required' },
    length: {
      maximum: 128,
    },
  },
  email: {
    presence: { allowEmpty: false, message: 'is required' },
    email: true,
    length: {
      maximum: 300,
    },
  },
  message: {
    presence: { allowEmpty: false, message: 'is required' },
  },
};

const ContactForm = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [formState, setFormState] = React.useState({
    isValid: false,
    values: {},
    touched: {},
    errors: {},
  });

  React.useEffect(() => {
    const errors = validate(formState.values, schema);

    setFormState((formState) => ({
      ...formState,
      isValid: !errors,
      errors: errors || {},
    }));
  }, [formState.values]);

  const handleChange = (event) => {
    event.persist();

    setFormState((formState) => ({
      ...formState,
      values: {
        ...formState.values,
        [event.target.name]:
          event.target.type === 'checkbox'
            ? event.target.checked
            : event.target.value,
      },
      touched: {
        ...formState.touched,
        [event.target.name]: true,
      },
    }));
  };

  const hasError = (field) =>
    formState.touched[field] && formState.errors[field] ? true : false;

  const handleOnSubmit = (event) => {
    event.preventDefault();
    const contactDetails = formState.values;

    axios
      .post('/sendEmail', contactDetails)
      .then((response) => {
        if (response.status === 200) {
          enqueueSnackbar('Message Sent Successfully.', { variant: 'success' });
        } else {
          enqueueSnackbar('Failed to send Message. Please try again later.', {
            variant: 'error',
          });
        }
      })
      .catch(() => {
        enqueueSnackbar('Failed to send Message. Please try again later.', {
          variant: 'error',
        });
      });

    setFormState({
      isValid: false,
      values: {},
      touched: {},
      errors: {},
    });
  };

  return (
    <Root>
      <form onSubmit={handleOnSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h4" align="center">
              <strong>Contact Us</strong>
            </Typography>
            <Typography variant="h6" color="textSecondary" align="center">
              We love to hear from you and will respond as soon as possible.
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              placeholder="Full Name"
              label="Full Name *"
              variant="outlined"
              size="medium"
              name="fullname"
              fullWidth
              helperText={hasError('fullname') ? formState.errors.fullname[0] : null}
              error={hasError('fullname')}
              onChange={handleChange}
              type="text"
              value={formState.values.fullname || ''}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              placeholder="Mobile"
              label="Mobile *"
              variant="outlined"
              size="medium"
              name="mobile"
              fullWidth
              helperText={hasError('mobile') ? formState.errors.mobile[0] : null}
              error={hasError('mobile')}
              onChange={handleChange}
              type="text"
              value={formState.values.mobile || ''}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              placeholder="E-mail"
              label="E-mail *"
              variant="outlined"
              size="medium"
              name="email"
              fullWidth
              helperText={hasError('email') ? formState.errors.email[0] : null}
              error={hasError('email')}
              onChange={handleChange}
              type="email"
              value={formState.values.email || ''}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              placeholder="Message"
              label="Message *"
              variant="outlined"
              name="message"
              fullWidth
              helperText={hasError('message') ? formState.errors.message[0] : null}
              error={hasError('message')}
              onChange={handleChange}
              multiline
              rows={4}
              value={formState.values.message || ''}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Fields marked with * are required.
            </Typography>
            <Button
              size="large"
              variant="contained"
              type="submit"
              color="primary"
              disabled={!formState.isValid}
            >
              Send
            </Button>
          </Grid>
        </Grid>
      </form>
    </Root>
  );
};

export default ContactForm;
